/**
 * API error taxonomy — mirrors `API_CONTRACT.md` §11.
 *
 * Each backend error response has the shape:
 *   { code, message, request_id, details? }
 * Backend codes map to a UI action (toast / redirect / inline form error /
 * blocking dialog) here. New codes added on the backend must be added here
 * in the same PR.
 */

export const API_ERROR_CODES = [
  "UNAUTHENTICATED",
  "TOKEN_EXPIRED",
  "FORBIDDEN",
  // Account-level access denials (BIO-227). The Cognito token is valid, but the
  // identity/access model refuses the session — re-authenticating won't help, so
  // these surface an explanatory dialog rather than a login redirect loop.
  "ACCOUNT_NOT_PROVISIONED",
  "ACCOUNT_DISABLED",
  "ORGANIZATION_DISABLED",
  "ACCESS_DISABLED",
  "EMAIL_UNVERIFIED",
  // BIO-363. The auth boundary's own spelling of "nothing resolved at all" - not the
  // product's `ACCOUNT_NOT_PROVISIONED`, which means "no product access row" for an
  // identity that did resolve. Absent from this list, it arrived as an unknown code
  // and rendered as a generic failure.
  "IDENTITY_NOT_PROVISIONED",
  // BIO-363. Same family, but refused on the sign-in's Entra tenant rather than
  // on the account: the tenant is not the one registered for the caller's
  // organization, or that organization has none registered. Checked per request,
  // so it can begin refusing a session that is already live.
  "TENANT_NOT_REGISTERED",
  // The caller holds compliance access in several organizations and has not
  // said which one this session acts in. Unlike the denials above this is
  // ANSWERABLE — `details.candidates` carries the organizations to choose from,
  // so it must stay out of `ACCOUNT_DENIAL_MESSAGES`: the UI shows a picker, not
  // a dead end with a sign-out button.
  "ORGANIZATION_SELECTION_REQUIRED",
  "AUDITOR_READ_ONLY",
  "REASON_REQUIRED",
  "IDEMPOTENCY_KEY_REQUIRED",
  "VALIDATION_ERROR",
  "BATCH_LOCKED",
  "REPORT_ALREADY_SIGNED",
  "CONFIG_VERSION_CONFLICT",
  "IDEMPOTENCY_CONFLICT",
  "DUPLICATE_UPLOAD_CONFLICT",
  "VALIDATION_BLOCKERS",
  "SUBSTITUTION_OVER_THRESHOLD",
  "UPLOAD_MALWARE_DETECTED",
  "RESOURCE_LOCKED",
  "RATE_LIMITED",
  "INTERNAL",
  "UNAVAILABLE",
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/**
 * Account-level access denials (BIO-227): the session is authentic, but the
 * identity/access model refuses it. Re-authenticating cannot help, so the UI shows
 * this message on an explanatory screen rather than retrying or redirecting to
 * /login — which for `TENANT_NOT_REGISTERED` would loop, because the handshake
 * succeeds and the next request is refused again.
 *
 * One export, read by `AccountInactive` for the copy and by `AuthProvider` for
 * membership, so adding a code here reaches both. Two copies of this list existed
 * once and had already drifted apart.
 */
export const ACCOUNT_DENIAL_MESSAGES: Record<
  | "ACCOUNT_NOT_PROVISIONED"
  | "IDENTITY_NOT_PROVISIONED"
  | "ACCOUNT_DISABLED"
  | "ORGANIZATION_DISABLED"
  | "ACCESS_DISABLED"
  | "EMAIL_UNVERIFIED"
  | "TENANT_NOT_REGISTERED",
  string
> = {
  // Nothing resolved at all, so there is no account to explain the state of. The copy
  // is the product's, because that is the only thing the reader can act on.
  IDENTITY_NOT_PROVISIONED:
    "Your account is not set up for the Compliance Platform. Contact a compliance admin.",
  ACCOUNT_NOT_PROVISIONED:
    "Your account isn't provisioned for this workspace yet. Ask a compliance admin to grant access.",
  ACCOUNT_DISABLED:
    "Your account has been disabled. Contact your administrator if you believe this is a mistake.",
  ORGANIZATION_DISABLED:
    "This organization's access has been suspended. Contact your administrator.",
  ACCESS_DISABLED:
    "Your access to the compliance workspace has been revoked. Contact a compliance admin.",
  EMAIL_UNVERIFIED: "Verify your email address with your identity provider before signing in.",
  TENANT_NOT_REGISTERED:
    "Your organization isn't enabled for Microsoft sign-in yet. Contact your OMai administrator.",
};

export const ACCOUNT_DENIAL_CODES: ReadonlySet<string> = new Set(
  Object.keys(ACCOUNT_DENIAL_MESSAGES),
);

export type ApiErrorEnvelope = {
  code: string;
  message: string;
  request_id: string;
  details?: Record<string, unknown>;
};

export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly requestId: string;
  readonly details: Record<string, unknown>;

  constructor(httpStatus: number, envelope: ApiErrorEnvelope) {
    super(envelope.message);
    this.name = "ApiError";
    this.httpStatus = httpStatus;
    this.code = envelope.code;
    this.requestId = envelope.request_id;
    this.details = envelope.details ?? {};
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export function isKnownCode(code: string): code is ApiErrorCode {
  return (API_ERROR_CODES as readonly string[]).includes(code);
}

/**
 * What one organization's screens call the parties around its custody point.
 *
 * Label text only — the wire vocabulary (`pge_statement`, `aemetis_fse_id`)
 * was coined for the reference customer and does not move; this is what a
 * person reads in its place. Carried on the grant so the acting organization's
 * labels come with the `/me` the SPA already holds.
 */
export type OrganizationBranding = {
  /** The utility whose custody-transfer meter settles the injected gas. */
  custody_party_label: string;
  /** The dispensing partner whose FSE identifiers the LCFS roster translates. */
  dispensing_partner_label: string;
};

/** The reference customer's labels — what a grant without branding means. */
export const DEFAULT_BRANDING: OrganizationBranding = {
  custody_party_label: "PG&E",
  dispensing_partner_label: "Trillium",
};

/** One organization a caller may act in, as `/me` and the picker see it. */
export type OrganizationGrant = {
  organization_id: string;
  name: string;
  role: string;
  /** Absent from a backend predating it, and from a refusal's candidates. */
  branding?: OrganizationBranding;
};

/**
 * The organizations offered by an `ORGANIZATION_SELECTION_REQUIRED` refusal.
 *
 * Returns `[]` for anything malformed rather than throwing: the caller's next
 * move is to render a picker, and a picker with no options is a state the UI can
 * report ("ask an administrator") where an exception mid-render is not.
 */
export function organizationCandidatesFrom(
  details: Record<string, unknown> | undefined,
): OrganizationGrant[] {
  const raw = details?.candidates;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { organization_id: id, name, role } = entry as Record<string, unknown>;
    if (typeof id !== "string" || typeof name !== "string") return [];
    return [{ organization_id: id, name, role: typeof role === "string" ? role : "" }];
  });
}
